import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import moment from 'moment';
import { useMutation, useLazyQuery } from '@apollo/client';
import { Autocomplete, TextField } from '@mui/material';

import CustomButton from '../../../components/CustomButton';
import { UPDATE_REWARD_V2 } from '../../../apollo/reward/Mutation';
import { GET_REWARDS_V2 } from '../../../apollo/reward/Query';
import { GET_PRODUCTS } from '../../../apollo/product/query';
import { GET_CATEGORYS } from '../../../apollo/category/query';

function ModalEditReward({
  show,
  onClose,
  editingReward,
  onSubmit,
  file,
  onUpload,
  userTypeLoading,
  userTypeData,
}) {
  const shopId = JSON.parse(localStorage.getItem('SHOP_DATA'))?.id || JSON.parse(localStorage.getItem('USER_DATA'))?.data?.shop?.id;
  const [updateRewardV2, { loading: updating }] = useMutation(UPDATE_REWARD_V2);

  const [loadProducts, { data: productData, loading: productLoading }] = useLazyQuery(GET_PRODUCTS);
  const [loadCategories, { data: categoryData, loading: categoryLoading }] = useLazyQuery(GET_CATEGORYS);

  const [productLimit, setProductLimit] = useState(100);
  const [categoryLimit, setCategoryLimit] = useState(100);
  const [productSearch, setProductSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [debouncedProductSearch, setDebouncedProductSearch] = useState('');
  const [debouncedCategorySearch, setDebouncedCategorySearch] = useState('');
  const [productCache, setProductCache] = useState([]);
  const [categoryCache, setCategoryCache] = useState([]);

  useEffect(() => {
    const fresh = productData?.products?.data || [];
    if (fresh.length === 0) return;
    setProductCache((prev) => {
      const map = new Map(prev.map((p) => [p.id, p]));
      fresh.forEach((p) => p?.id && map.set(p.id, p));
      return Array.from(map.values());
    });
  }, [productData]);

  useEffect(() => {
    const fresh = categoryData?.categories?.data || [];
    if (fresh.length === 0) return;
    setCategoryCache((prev) => {
      const map = new Map(prev.map((c) => [c.id, c]));
      fresh.forEach((c) => c?.id && map.set(c.id, c));
      return Array.from(map.values());
    });
  }, [categoryData]);

  useEffect(() => {
    const seed = (editingReward?.applicableProducts || []).filter((p) => p && typeof p === 'object' && p.id);
    if (seed.length > 0) {
      setProductCache((prev) => {
        const map = new Map(prev.map((p) => [p.id, p]));
        seed.forEach((p) => map.set(p.id, p));
        return Array.from(map.values());
      });
    }
    const seedC = (editingReward?.applicableCategories || []).filter((c) => c && typeof c === 'object' && c.id);
    if (seedC.length > 0) {
      setCategoryCache((prev) => {
        const map = new Map(prev.map((c) => [c.id, c]));
        seedC.forEach((c) => map.set(c.id, c));
        return Array.from(map.values());
      });
    }
  }, [editingReward]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedProductSearch(productSearch), 400);
    return () => clearTimeout(t);
  }, [productSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedCategorySearch(categorySearch), 400);
    return () => clearTimeout(t);
  }, [categorySearch]);

  useEffect(() => {
    if (show) {
      const searching = !!debouncedProductSearch;
      loadProducts({
        variables: {
          limit: searching ? 100000 : productLimit,
          where: { shop: shopId, isDeleted: false, ...(searching ? { name: debouncedProductSearch } : {}) },
        },
        fetchPolicy: 'network-only',
      });
    }
  }, [show, shopId, productLimit, debouncedProductSearch, loadProducts]);

  useEffect(() => {
    if (show) {
      const searching = !!debouncedCategorySearch;
      loadCategories({
        variables: {
          limit: searching ? 100000 : categoryLimit,
          where: { shop: shopId, isDeleted: false, ...(searching ? { name: debouncedCategorySearch } : {}) },
        },
        fetchPolicy: 'network-only',
      });
    }
  }, [show, shopId, categoryLimit, debouncedCategorySearch, loadCategories]);

  const rankByQuery = (list, q) => {
    if (!q) return list;
    const ql = q.toLowerCase().trim();
    if (!ql) return list;
    const score = (name) => {
      const n = (name || '').toLowerCase();
      if (n === ql) return 0;
      if (n.startsWith(ql)) return 1;
      if (n.includes(ql)) return 2;
      return 3;
    };
    return [...list].sort((a, b) => score(a.name) - score(b.name));
  };

  const productOptions = useMemo(() => {
    const fresh = productData?.products?.data || [];
    if (debouncedProductSearch) {
      // actively searching: show ONLY server search results
      return rankByQuery(fresh, productSearch);
    }
    // not searching: show everything from cache (server list + previously loaded)
    const freshIds = new Set(fresh.map((p) => p.id));
    const extras = (productCache || []).filter((p) => p?.id && !freshIds.has(p.id));
    return [...fresh, ...extras];
  }, [productData, productCache, productSearch, debouncedProductSearch]);

  const categoryOptions = useMemo(() => {
    const fresh = categoryData?.categories?.data || [];
    if (debouncedCategorySearch) {
      return rankByQuery(fresh, categorySearch);
    }
    const freshIds = new Set(fresh.map((c) => c.id));
    const extras = (categoryCache || []).filter((c) => c?.id && !freshIds.has(c.id));
    return [...fresh, ...extras];
  }, [categoryData, categoryCache, categorySearch, debouncedCategorySearch]);

  const submitUpdateReward = async (values, { setSubmitting }) => {
    try {
      // Combine date + time to ISO string
      const combineDT = (dateStr, timeStr, defaultTime) => {
        if (!dateStr && !timeStr) return null;
        const time = timeStr || defaultTime;
        const d = new Date(`${dateStr}T${time}`);
        return isNaN(d) ? null : d.toISOString();
      };

      const payload = {
        name: values.name,
        code: values.code || null,
        description: values.description || '',
        percent: values.percent !== '' && values.percent != null ? Number(values.percent) : null,
        pointMax: values.pointMax !== '' && values.pointMax != null ? Number(values.pointMax) : null,
        pointMin: values.pointMin !== '' && values.pointMin != null ? Number(values.pointMin) : null,
        maxDiscountAmount: values.maxDiscountAmount !== '' && values.maxDiscountAmount != null ? Number(values.maxDiscountAmount) : null,
        maxUser: values.maxUser !== '' && values.maxUser != null ? Number(values.maxUser) : null,
        remainUser: values.remainUser !== '' && values.remainUser != null ? Number(values.remainUser) : null,
        minimumOrderAmount: values.minimumOrderAmount !== '' && values.minimumOrderAmount != null ? Number(values.minimumOrderAmount) : null,
        minimumOrderQuantity: values.minimumOrderQuantity !== '' && values.minimumOrderQuantity != null ? Number(values.minimumOrderQuantity) : null,
        freeItemQuantity: values.freeItemQuantity !== '' && values.freeItemQuantity != null ? Number(values.freeItemQuantity) : null,
        rewardType: values.rewardType,
        applyType: values.applyType,
        applicableProducts: values.applyType === 'SPECIFIC_PRODUCTS' ? values.applicableProducts : [],
        applicableCategories: values.applyType === 'SPECIFIC_CATEGORIES' ? values.applicableCategories : [],
        userType: values.userType || null,
        startDate: combineDT(
          values.startDate,
          values.startHour && values.startMinute ? `${values.startHour}:${values.startMinute}` : null,
          '00:00'
        ),
        endDate: combineDT(
          values.endDate,
          values.endHour && values.endMinute ? `${values.endHour}:${values.endMinute}` : null,
          '23:59'
        ),
        status: values.status,
        platform: values.platform || 'POS',
      };

      console.log('Update Reward Payload:', payload);
      console.log('Status value:', values.status);

      const result = await updateRewardV2({
        variables: { data: payload, where: { id: editingReward?.id } },
        optimisticResponse: {
          updateRewardV2: {
            __typename: 'RewardV2',
            id: editingReward?.id,
            name: payload.name,
            code: payload.code,
            description: payload.description,
            maxDiscountAmount: payload.maxDiscountAmount,
            rewardType: payload.rewardType,
            applyType: payload.applyType,
            applicableProducts: payload.applicableProducts,
            applicableCategories: payload.applicableCategories,
            minimumOrderAmount: payload.minimumOrderAmount,
            minimumOrderQuantity: payload.minimumOrderQuantity,
            freeItemQuantity: payload.freeItemQuantity,
            percent: payload.percent,
            pointMax: payload.pointMax,
            pointMin: payload.pointMin,
            remainUser: payload.remainUser,
            userType: payload.userType,
            maxUser: payload.maxUser,
            status: payload.status,
            startDate: payload.startDate,
            endDate: payload.endDate,
            platform: payload.platform,
          },
        },
        update: (cache, { data }) => {
          const updatedFields = data?.updateRewardV2;
          console.log('Updated fields from mutation:', updatedFields);
          if (!updatedFields || !editingReward?.id) return;
          try {
            const existing = cache.readQuery({ query: GET_REWARDS_V2 });
            if (existing?.getRewardsV2?.data) {
              const updatedData = existing.getRewardsV2.data.map((r) =>
                r.id === editingReward.id
                  ? { ...r, ...updatedFields }
                  : r
              );
              cache.writeQuery({
                query: GET_REWARDS_V2,
                data: {
                  getRewardsV2: {
                    ...existing.getRewardsV2,
                    data: updatedData,
                  },
                },
              });
            }
          } catch (err) {
            console.error('Cache update error:', err);
          }
        },
        refetchQueries: [{ query: GET_REWARDS_V2 }],
        awaitRefetchQueries: true,
      });

      console.log('Update Reward Result:', result);

      setSubmitting(false);
      if (typeof onSubmit === 'function') {
        onSubmit(values);
      }
      if (typeof onClose === 'function') {
        onClose();
      }
    } catch (e) {
      setSubmitting(false);
      console.error('UPDATE_REWARD_V2, error', e);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>ແກ້ໄຂລາງວັນ</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={{
            name: editingReward?.name || '',
            code: editingReward?.code || '',
            pointMin: editingReward?.pointMin ?? '',
            pointMax: editingReward?.pointMax ?? '',
            percent: editingReward?.percent || '',
            maxDiscountAmount: editingReward?.maxDiscountAmount || '',
            maxUser: editingReward?.maxUser || '',
            remainUser: editingReward?.remainUser || '',
            minimumOrderAmount: editingReward?.minimumOrderAmount || '',
            minimumOrderQuantity: editingReward?.minimumOrderQuantity || '',
            freeItemQuantity: editingReward?.freeItemQuantity || '',
            rewardType: editingReward?.rewardType || 'DISCOUNT',
            applyType: editingReward?.applyType || 'ALL',
            autoGenerateCode: false,
            applicableProducts: editingReward?.applicableProducts?.map(i => i.id || i) || [],
            applicableCategories: editingReward?.applicableCategories?.map(i => i.id || i) || [],
            startDate: editingReward?.startDate ? moment(editingReward.startDate).format('YYYY-MM-DD') : '',
            endDate: editingReward?.endDate ? moment(editingReward.endDate).format('YYYY-MM-DD') : '',
            // add 24h time selection (hour/minute) to edit form
            startHour: editingReward?.startDate ? moment(editingReward.startDate).format('HH') : '00',
            startMinute: editingReward?.startDate ? moment(editingReward.startDate).format('mm') : '00',
            endHour: editingReward?.endDate ? moment(editingReward.endDate).format('HH') : '23',
            endMinute: editingReward?.endDate ? moment(editingReward.endDate).format('mm') : '59',
            description: editingReward?.description || '',
            status: editingReward?.status || 'USABLE',
            userType: editingReward?.userType?.id || '',
            platform: editingReward?.platform || 'POS',
          }}
          onSubmit={submitUpdateReward}
          enableReinitialize
        >
          {({ values, handleChange, handleSubmit, setFieldValue }) => (
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຊື່ລາງວັນ <span style={{color: 'red'}}>*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      placeholder="ກະລຸນາໃສ່ຊື່ລາງວັນ"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <Form.Label>ລະຫັດລາງວັນ</Form.Label>
                      <Form.Check 
                        type="checkbox"
                        label="ສ້າງອໍໂຕ້"
                        name="autoGenerateCode"
                        checked={values.autoGenerateCode}
                        onChange={handleChange}
                        id="edit-auto-gen-code"
                      />
                    </div>
                    <Form.Control
                      type="text"
                      name="code"
                      value={values.code}
                      onChange={handleChange}
                      placeholder={values.autoGenerateCode ? "ລະຫັດຈະຖືກສ້າງອໍໂຕ້" : "ກະລຸນາໃສ່ລະຫັດລາງວັນ"}
                      disabled={values.autoGenerateCode}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຄະແນນ</Form.Label>
                    <Form.Control
                      type="number"
                      name="pointMin"
                      style={{ width: '100%' }}
                      value={values.pointMin}
                      onChange={handleChange}
                      placeholder="ກະລຸນາໃສ່ຄະແນນທີ່ຕ້ອງແລກ"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ປະເພດລາງວັນ <span style={{color: 'red'}}>*</span></Form.Label>
                    <Form.Select
                      name="rewardType"
                      value={values.rewardType}
                      onChange={handleChange}
                      disabled={editingReward?.rewardType === 'DISCOUNT'}
                      required
                    >
                      <option value="DISCOUNT">ສ່ວນຫຼຸດ</option>
                      <option value="ITEM">ສິນຄ້າ</option>
                      <option value="FREE_SHIPPING">ສົ່ງຟຣີ</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຈຳນວນລາງວັນ</Form.Label>
                    <Form.Control
                      type="number"
                      name="maxUser"
                      style={{ width: '100%' }}
                      value={values.maxUser}
                      onChange={handleChange}
                      placeholder="ກະລຸນາໃສ່ຈຳນວນລາງວັນ"
                      min="1"
                    />
                  </Form.Group>
                </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>ຍອດຊື້ຂັ້ນຕ່ຳ (ກີບ)</Form.Label>
                      <Form.Control
                        type="number"
                        name="minimumOrderAmount"
                        style={{ width: '100%' }}
                        value={values.minimumOrderAmount}
                        onChange={handleChange}
                        placeholder="ກະລຸນາປ້ອນຍອດຊື້ຂັ້ນຕ່ຳ"
                        min="0"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>ຈຳນວນຊື້ຂັ້ນຕ່ຳ</Form.Label>
                      <Form.Control
                        type="number"
                        name="minimumOrderQuantity"
                        style={{ width: '100%' }}
                        value={values.minimumOrderQuantity}
                        onChange={handleChange}
                        placeholder="ກະລຸນາປ້ອນຈຳນວນຊື້ຂັ້ນຕ່ຳ"
                        min="0"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>ຈຳນວນທີ່ແຖມ/ຟຣີ</Form.Label>
                      <Form.Control
                        type="number"
                        name="freeItemQuantity"
                        style={{ width: '100%' }}
                        value={values.freeItemQuantity}
                        onChange={handleChange}
                        placeholder="ກະລຸນາປ້ອນຈຳນວນທີ່ແຖມ"
                        min="0"
                      />
                    </Form.Group>
                  </Col>
                </Row>


              <Row>
                {/* <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຄະແນນສູງສຸດ *</Form.Label>
                    <Form.Control
                      type="number"
                      name="pointMax"
                      style={{ width: '100%' }}
                      value={values.pointMax}
                      onChange={handleChange}
                      placeholder="ກະລຸນາໃສ່ຄະແນນສູງສຸດ"
                    />
                  </Form.Group>
                </Col> */}
                {
                  values.rewardType === 'DISCOUNT' && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>ເປີເຊັນສ່ວນຫຼຸດ (%)</Form.Label>
                        <Form.Control
                          type="number"
                          name="percent"
                          style={{ width: '100%' }}
                          value={values.percent}
                          onChange={handleChange}
                          placeholder="ກະລຸນາໃສ່ເປີເຊັນສ່ວນຫຼຸດ"
                          min="0"
                          max="100"
                        />
                      </Form.Group>
                    </Col>
                  )
                }
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຈຳນວນລາງວັນທີ່ເຫຼືອ</Form.Label>
                    <Form.Control
                      type="number"
                      name="remainUser"
                      style={{ width: '100%' }}
                      value={values.remainUser}
                      onChange={handleChange}
                      placeholder="ກະລຸນາໃສ່ຈຳນວນລາງວັນທີ່ເຫຼືອ"
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ປະເພດລູກຄ້າ</Form.Label>
                    <Form.Select
                      name="userType"
                      value={values.userType}
                      onChange={handleChange}
                      aria-label="Select user type"
                      disabled={userTypeLoading}
                    >
                      <option value="">
                        {userTypeLoading 
                          ? 'ກຳລັງໂຫຼດຂໍ້ມູນ...' 
                          : 'ເລືອກປະເພດລູກຄ້າ...'}
                      </option>
                      {!userTypeLoading && userTypeData?.getUserTypes
                        ?.filter((item) => item?.isOpen || item?.id === editingReward?.userType?.id)
                        .map((userType) => (
                          <option key={userType?.id} value={userType?.id}>
                            {userType?.name}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>ປະເພດການນຳໃຊ້</Form.Label>
                    <Form.Select
                      name="applyType"
                      value={values.applyType}
                      onChange={handleChange}
                    >
                      <option value="ALL">ທັງໝົດ</option>
                      <option value="SPECIFIC_PRODUCTS">ສິນຄ້າທີ່ກ່ຽວຂ້ອງ</option>
                      {/* <option value="SPECIFIC_CATEGORIES">ໝວດໝູ່ທີ່ກ່ຽວຂ້ອງ</option> */}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ແພລດຟອມ</Form.Label>
                    <Form.Select
                      name="platform"
                      value={values.platform}
                      onChange={handleChange}
                    >
                      {/* <option value="JUST_CAN">JUST CAN</option>
                      <option value="CF">CF</option> */}
                      <option value="POS">POS</option>
                      <option value="SALE_PAGE">SALE PAGE</option>
                      {/* <option value="AFFILIATE">AFFILIATE</option> */}
                      <option value="ALL">ທັງໝົດ</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {values.applyType === 'SPECIFIC_PRODUCTS' && (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Form.Label className="mb-0">ສິນຄ້າທີ່ກ່ຽວຂ້ອງ</Form.Label>
                        <Form.Select
                          size="sm"
                          style={{ width: 'auto' }}
                          value={productLimit}
                          onChange={(e) => setProductLimit(Number(e.target.value))}
                        >
                          <option value={100}>100</option>
                          <option value={150}>150</option>
                          <option value={200}>200</option>
                          <option value={500}>500</option>
                        </Form.Select>
                      </div>
                      <Autocomplete
                        multiple
                        options={productOptions}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        getOptionLabel={(option) => option.name || ""}
                        value={values.applicableProducts
                          .map((id) => productCache.find((p) => p.id === id) || { id, name: id })
                          .filter(Boolean)}
                        onChange={(event, newValue) => {
                          setFieldValue('applicableProducts', newValue.map(item => item.id));
                        }}
                        loading={productLoading}
                        inputValue={productSearch}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === 'input' || reason === 'clear') {
                            setProductSearch(newInputValue);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="ເລືອກສິນຄ້າ"
                            variant="outlined"
                            size="small"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.stopPropagation();
                                setDebouncedProductSearch(productSearch);
                              }
                            }}
                          />
                        )}
                        filterOptions={(x) => x}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {values.applyType === 'SPECIFIC_CATEGORIES' && (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Form.Label className="mb-0">ໝວດໝູ່ສິນຄ້າທີ່ກ່ຽວຂ້ອງ</Form.Label>
                        <Form.Select
                          size="sm"
                          style={{ width: 'auto' }}
                          value={categoryLimit}
                          onChange={(e) => setCategoryLimit(Number(e.target.value))}
                        >
                          <option value={100}>100</option>
                          <option value={150}>150</option>
                          <option value={200}>200</option>
                          <option value={500}>500</option>
                        </Form.Select>
                      </div>
                      <Autocomplete
                        multiple
                        options={categoryOptions}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        getOptionLabel={(option) => option.name || ""}
                        value={values.applicableCategories
                          .map((id) => categoryCache.find((c) => c.id === id) || { id, name: id })
                          .filter(Boolean)}
                        onChange={(event, newValue) => {
                          setFieldValue('applicableCategories', newValue.map(item => item.id));
                        }}
                        loading={categoryLoading}
                        inputValue={categorySearch}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === 'input' || reason === 'clear') {
                            setCategorySearch(newInputValue);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="ເລືອກໝວດໝູ່"
                            variant="outlined"
                            size="small"
                          />
                        )}
                        filterOptions={(x) => x}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}

              <Form.Group className="mb-3">
                <Form.Label>ລາຍລະອຽດ</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  placeholder="ກະລຸນາໃສ່ລາຍລະອຽດຂອງລາງວັນ"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ວັນທີເລີ່ມຕົ້ນ *</Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={values.startDate}
                      onChange={handleChange}
                      required
                    />
                    {/* time input for start (24-hour select) */}
                    <Form.Group className="mt-2">
                      <Form.Label>ເວລາເລີ່ມຕົ້ນ</Form.Label>
                      <Row>
                        <Col>
                          <Form.Select
                            name="startHour"
                            value={values.startHour}
                            onChange={handleChange}
                            aria-label="Hour"
                          >
                            {[...Array(24).keys()].map((h) => (
                              <option key={h} value={String(h).padStart(2, '0')}>
                                {String(h).padStart(2, '0')}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col>
                          <Form.Select
                            name="startMinute"
                            value={values.startMinute}
                            onChange={handleChange}
                            aria-label="Minute"
                          >
                            {[...Array(60).keys()].map((m) => (
                              <option key={m} value={String(m).padStart(2, '0')}>
                                {String(m).padStart(2, '0')}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                      </Row>
                    </Form.Group>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ວັນທີສິ້ນສຸດ *</Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={values.endDate}
                      onChange={handleChange}
                      required
                    />
                    {/* time input for end (24-hour select) */}
                    <Form.Group className="mt-2">
                      <Form.Label>ເວລາສິ້ນສຸດ</Form.Label>
                      <Row>
                        <Col>
                          <Form.Select
                            name="endHour"
                            value={values.endHour}
                            onChange={handleChange}
                            aria-label="Hour"
                          >
                            {[...Array(24).keys()].map((h) => (
                              <option key={h} value={String(h).padStart(2, '0')}>
                                {String(h).padStart(2, '0')}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col>
                          <Form.Select
                            name="endMinute"
                            value={values.endMinute}
                            onChange={handleChange}
                            aria-label="Minute"
                          >
                            {[...Array(60).keys()].map((m) => (
                              <option key={m} value={String(m).padStart(2, '0')}>
                                {String(m).padStart(2, '0')}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                      </Row>
                    </Form.Group>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>ສະຖານະ</Form.Label>
                      <Form.Select
                        name="status"
                        value={values.status}
                        onChange={handleChange}
                      >
                        <option value="USABLE">ໃຊ້ງານໄດ້</option>
                        <option value="EXPIRED">ໝົດອາຍຸ</option>
                        <option value="OUT_OF_STOCK">ເຄື່ອງໝົດ</option>
                        <option value="DISABLE">ປິດການໃຊ້ງານ</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {
                    values.rewardType !== 'ITEM' && <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>ຫຼຸດສູງສຸດ (ກີບ)</Form.Label>
                      <Form.Control
                        type="number"
                        name="maxDiscountAmount"
                        style={{ width: '100%' }}
                        value={values.maxDiscountAmount}
                        onChange={handleChange}
                        placeholder="ກະລຸນາໃສ່ຈຳນວນຫຼຸດສູງສຸດ"
                        min="0"
                        disabled={values.rewardType === 'ITEM'}
                      />
                    </Form.Group>
                  </Col>
                  }
                </Row>

              {/* <Form.Group className="mb-3">
                <Form.Label>ຮູບພາບລາງວັນ</Form.Label>
                <div style={{ textAlign: 'center' }}>
                  <input
                    type="file"
                    id="file-upload"
                    onChange={onUpload}
                    accept="image/*"
                    hidden
                  />
                  <label style={{ width: 200 }} htmlFor="file-upload">
                    <div
                      style={{
                        backgroundColor: '#F9FAFB',
                        height: '200px',
                        width: '100%',
                        border: '1px dashed #93989F',
                        borderRadius: 8,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}>
                      {file ? (
                        <ImageThumb image={file} />
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}>
                          <CloudUploadOutlinedIcon
                            sx={{ fontSize: 50, color: '#93989F' }}
                          />
                          <p style={{ color: '#93989F' }}>ເພີ່ມຮູບພາບລາງວັນ</p>
                          <p
                            style={{
                              color: '#3C169B',
                              textDecoration: 'underline',
                            }}>
                            Browse
                          </p>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </Form.Group> */}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <Button variant="secondary" onClick={onClose}>
                  ຍົກເລີກ
                </Button>
                <CustomButton
                  type="submit"
                  disabled={updating}
                  title={updating ? <Spinner/> : 'ອັບເດດ'}
                  width="130px"
                  borderRadius="8px"
                />
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
}

export default ModalEditReward;
